import { Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { FieldType, NumberField, TextField } from '../../../store/models/field-models';
import { isMaxLengthRule, ValidationRuleType } from '../../../store/models/validator-models';
import { extractFieldErrorMessages } from '../utils';
import { FieldUtilsService } from '../field-utils.service';

@Component({
    selector: 'app-text-field',
    templateUrl: './text-field.component.html',
    styleUrls: ['./text-field.component.scss'],
})
export class TextFieldComponent {
    @Input() field: TextField | NumberField;
    @Input() control: FormControl;
    @Input() fieldId: string;

    FieldType = FieldType;
    constructor(private store: Store, private translate: TranslateService, public fieldUtils: FieldUtilsService) {}

    get isVisible(): boolean {
        return this.field?.visible !== false;
    }

    get resolvedInputType(): FieldType.Text | FieldType.Number {
        if (this.field.type === FieldType.Number) {
            return this.field.inputType === FieldType.Number ? FieldType.Number : FieldType.Text;
        }
        return FieldType.Text;
    }

    get containerClass(): string {
        const base = this.field.panelClass || '';
        return this.resolvedInputType === FieldType.Number ? `${base} native-number-input` : base;
    }

    get isRequired(): boolean {
        return this.field?.validators?.some((v) => v.type === ValidationRuleType.Required) || false;
    }

    get hasError(): boolean {
        return this.control?.invalid && (this.control?.touched || this.control?.dirty);
    }

    get maxLength(): number | null {
        const maxLengthValidator = this.field?.validators?.find(isMaxLengthRule);
        return maxLengthValidator ? maxLengthValidator.value : null;
    }

    get errorMessages(): string[] {
        return extractFieldErrorMessages(this.control, this.field, this.translate);
    }

    get isIntegerOnly(): boolean {
        return this.field.type === FieldType.Number && this.field.integerOnly === true;
    }

    get minValue(): number | null {
        return this.field.type === FieldType.Number && this.field.min !== undefined ? this.field.min : null;
    }

    get maxValue(): number | null {
        return this.field.type === FieldType.Number && this.field.max !== undefined ? this.field.max : null;
    }

    get stepValue(): number | null {
        return this.field.type === FieldType.Number && this.field.step !== undefined ? this.field.step : null;
    }

    onKeyDown(event: KeyboardEvent): void {
        const allowedControlKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'];

        if (this.field.allowEmailCharacters) {
            if (allowedControlKeys.includes(event.key)) return;

            const allowed = /^[A-Za-z0-9.!#$%&'*+\-/=?^_`{|}~@]$/;

            if (!allowed.test(event.key)) {
                event.preventDefault();
            }
        }

        if (this.field.allowPhoneCharacters) {
            if (allowedControlKeys.includes(event.key) || event.ctrlKey || event.metaKey) return;
            if (/^[0-9]$/.test(event.key)) return;

            if (event.key === '+') {
                const target = event.target;

                if (target instanceof HTMLInputElement) {
                    const selectionStart = target.selectionStart ?? 0;
                    const selectionEnd = target.selectionEnd ?? selectionStart;
                    const valueAfterInput = `${target.value.slice(0, selectionStart)}+${target.value.slice(
                        selectionEnd,
                    )}`;

                    if (selectionStart === 0 && !valueAfterInput.slice(1).includes('+')) {
                        return;
                    }
                }
            }

            event.preventDefault();
            return;
        }

        if (this.field.type !== FieldType.Number) {
            return;
        }

        if (this.field.blockNonNumeric) {
            if (allowedControlKeys.includes(event.key)) return;
            if (!/^[0-9]$/.test(event.key)) {
                event.preventDefault();
            }
        }

        if (event.key === 'e' || event.key === 'E') {
            event.preventDefault();
            return;
        }

        if (this.isIntegerOnly && (event.key === '.' || event.key === ',')) {
            event.preventDefault();
            return;
        }
        if (this.minValue !== null && this.minValue >= 0 && (event.key === '-' || event.key === '+')) {
            event.preventDefault();
        }
    }

    private canFormatDecimalPlaces(value: string, decimalPlaces: number): boolean {
        const decimalPlacesPattern = new RegExp(
            `^-?(?:\\d+(?:\\.\\d{0,${decimalPlaces}})?|\\.\\d{1,${decimalPlaces}})$`,
        );
        return decimalPlacesPattern.test(value);
    }

    onTextChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        let value = target.value;

        if (this.field.type === FieldType.Number) {
            value = value.replace(/\s/g, '');
            if (this.isIntegerOnly && value !== '') {
                value =
                    this.minValue !== null && this.minValue >= 0
                        ? value.replace(/[^0-9]/g, '')
                        : value.replace(/(?!^-)[^0-9]/g, '');
            }
            if (value !== '' && !Number.isNaN(Number(value))) {
                const numeric = Number(value);
                if (this.minValue !== null && numeric < this.minValue) {
                    value = String(this.minValue);
                } else if (this.maxValue !== null && numeric > this.maxValue) {
                    value = String(this.maxValue);
                }

                if (
                    this.field.formatDecimalPlaces === true &&
                    this.field.decimalPlaces !== undefined &&
                    this.canFormatDecimalPlaces(value, this.field.decimalPlaces)
                ) {
                    value = Number(value).toFixed(this.field.decimalPlaces);
                }
            }
        } else if (!this.field.allowEmailCharacters) {
            value = value.trim();
        }

        this.control.setValue(value);

        if (this.field.events?.onChange) {
            this.field.events.onChange
                .call(this.field, this.field.type === FieldType.Number ? (value === '' ? null : Number(value)) : value)
                .forEach((action) => this.store.dispatch(action));
        }
    }
}
